import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { Column } from 'react-table';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useEnableLotTracking } from '../../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { openProductDetailModal } from '../../../../../../store/products/products.actions';
import type { TransferOrderProduct } from '../../../api/transfer-order-api';
import { ProductGridFooter } from '../../../components/create-transfer-order-drawer/components/product-grid-footer/product-grid-footer';
import { messages } from './messages';
import { TransferOrderProgressBar } from '../transfer-order-progress-bar/transfer-order-progress-bar';
import { getProductGridColumns } from './product-grid-columns';
import styles from './transferred-products.styles.module.less';

type FlattenedProductRow = TransferOrderProduct & {
  isFirstProductRow: boolean;
  isLastProductRow: boolean;
  lot: string | null;
};

type TransferredProductsProps = {
  products: TransferOrderProduct[];
};

const createGroupKey = (productId: number, lotUuid: string | null): string =>
  lotUuid ? `${productId}::${lotUuid}` : `${productId}::no-lot`;

const flattenProducts = (products: TransferOrderProduct[]): FlattenedProductRow[] => {
  const groupMap = new Map<string, TransferOrderProduct[]>();

  products.forEach((product) => {
    const groupKey = createGroupKey(product.productId, product.lotUuid);
    const existing = groupMap.get(groupKey) ?? [];
    groupMap.set(groupKey, [...existing, product]);
  });

  const grouped: FlattenedProductRow[] = [];
  const productRowCounts = new Map<number, number>();
  const productRowIndices = new Map<number, number>();

  groupMap.forEach((rows) => {
    const productId = rows[0].productId;
    productRowCounts.set(productId, (productRowCounts.get(productId) ?? 0) + 1);
  });

  groupMap.forEach((rows) => {
    const firstRow = rows[0];
    const productId = firstRow.productId;
    const currentIndex = productRowIndices.get(productId) ?? 0;
    const totalRows = productRowCounts.get(productId) ?? 1;
    productRowIndices.set(productId, currentIndex + 1);

    grouped.push({
      ...firstRow,
      quantity: rows.reduce((sum, r) => sum + (r.quantity || 0), 0),
      totalReceived: rows.reduce((sum, r) => sum + (r.totalReceived || 0), 0),
      totalRejected: rows.reduce((sum, r) => sum + (r.totalRejected || 0), 0),
      isFirstProductRow: currentIndex === 0,
      isLastProductRow: currentIndex === totalRows - 1,
      lot: firstRow.lot,
    });
  });

  return grouped;
};

export const TransferredProducts = ({ products }: TransferredProductsProps) => {
  const dispatch = useDispatch();
  const isLotTrackingEnabled = useEnableLotTracking();

  const flattenedProducts = useMemo(() => flattenProducts(products), [products]);
  const totalOrdered = products.reduce((sum, product) => sum + (product.quantity || 0), 0);
  const totalReceived = products.reduce((sum, product) => sum + (product.totalReceived || 0), 0);
  const totalRejected = products.reduce((sum, product) => sum + (product.totalRejected || 0), 0);

  const openProductDetails = useCallback(
    (productId: number) => {
      if (!productId) return;
      dispatch(openProductDetailModal(productId));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => getProductGridColumns(openProductDetails, isLotTrackingEnabled) as Column<FlattenedProductRow>[],
    [openProductDetails, isLotTrackingEnabled],
  );

  const getBodyRowClassName = useCallback(
    (row: { original: FlattenedProductRow }) => (row.original.isLastProductRow ? '' : styles['no-bottom-border']),
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: flattenedProducts,
    },
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  const tableWrapperProps = {
    className: styles.table,
    emptyRow: <Text className={styles['empty-row-text']} textType={TextType.Paragraph} value={messages.noProductsPlaceholder} />,
    getBodyRowClassName,
    getTableBodyProps,
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
  };

  return (
    <div className={styles.container}>
      <Text className={styles['section-title']} textType={TextType.H4} value={messages.transferredProducts} />
      <TransferOrderProgressBar receivedItems={totalReceived} rejectedItems={totalRejected} totalItems={totalOrdered} />

      <div className={styles['table-container']}>
        <ReactTableWrapper {...tableWrapperProps} />
        {flattenedProducts.length > 0 && <ProductGridFooter totalProducts={flattenedProducts.length} />}
      </div>
    </div>
  );
};
